// ---------------------------------------------------------------------------
// useBlocksV3 — hook de carga + polling dos blocos de calculo do mes.
// ---------------------------------------------------------------------------
// Consumido por ``BlocksPane`` do ``ProgressDrawerV3``.
//
// Polling adaptativo (PA-009; revisado na leva 101):
//   - cadencia ``rapida`` (~4s) enquanto houver bloco em estado vivo
//     (``running``/``waiting``/``stale``);
//   - cadencia ``lenta`` (~30s) quando todos os blocos estao em terminal
//     (``done``/``failed``) — antes ficavamos em "sleep eterno" ate
//     refetch manual, o que produzia divergencia visivel com o badge
//     do mes (``useProgressOverviewV3`` continuava pollando a cada 60s,
//     entao o badge atualizava sozinho para "reprocessamento pendente"
//     enquanto o BlocksPane mostrava tudo concluido). Com 304+ETag o
//     custo de pollar lento e' baixissimo;
//   - sinal externo ``monthStatusHint`` (opcional): quando o caller
//     detecta drift via outro endpoint (badge passou a ``stale``,
//     ``processing``, ``waiting`` etc.) dispara refetch imediato sem
//     esperar a cadencia lenta;
//   - cada poll envia ``If-None-Match`` com o ETag previo. 304 do
//     backend significa "nada mudou no mes" — preservamos os blocos
//     anteriores.
// ---------------------------------------------------------------------------

import { useEffect, useRef, useState } from "react";

import { fetchBlocksV3 } from "../../../../services/inputsV3Service";

const POLL_FAST_MS = 4000;
const POLL_SLOW_MS = 30_000;

// Onda 3.1: bucket UI ``running`` foi renomeado para ``processing``
// (alinhado a CSL/BLK/InputControl). Mantemos ambas as chaves durante
// a transicao para compat com snapshots cacheados.
const ALIVE_STATUSES = new Set(["processing", "running", "waiting", "stale"]);

export function useBlocksV3({
  year,
  month,
  enabled = true,
  onMonthStatusChange,
  monthStatusHint = null,
}) {
  const canFetch = !!(enabled && year && month);

  const [state, setState] = useState({
    loading: false,
    blocks: [],
    summary: null,
    monthStatus: null,
    error: null,
    snapshotSource: null,
    userIsDev: false,
  });

  const etagRef = useRef(null);
  const blocksRef = useRef([]);
  // ``onMonthStatusChange`` ref: o callback pode mudar de identidade a
  // cada render do consumer (e' comum no uso ``onMonthStatusChange={...}
  // sem useCallback``). Guardar em ref evita reagendar o effect de
  // polling toda vez que o consumer re-renderiza.
  const onMonthStatusChangeRef = useRef(onMonthStatusChange);
  useEffect(() => {
    onMonthStatusChangeRef.current = onMonthStatusChange;
  }, [onMonthStatusChange]);
  useEffect(() => {
    blocksRef.current = state.blocks;
  }, [state.blocks]);

  // Onda 1.5: ``yearRef``/``monthRef``/``mountedRef``: ``applyPayload``
  // historicamente capturava ``year``/``month`` por closure no momento
  // da declaracao (a cada render). Se o usuario trocava o mes
  // EXATAMENTE durante uma request em voo, a closure antiga ainda
  // emitia ``onMonthStatusChange({year: VELHO, month: VELHO, status:
  // NOVO_MES})`` — o consumer (InputsV3) interpretava como "o mes que
  // estou olhando mudou de status" quando na verdade era o mes
  // anterior. Resultado: badge piscando para um estado que nao era do
  // mes corrente. Usar refs garante que o callback sempre recebe os
  // valores frescos do periodo selecionado AGORA.
  //
  // ``mountedRef`` impede setState/callback apos unmount (defesa
  // contra promessas em voo quando o usuario fecha o ProgressDrawer).
  const yearRef = useRef(year);
  const monthRef = useRef(month);
  useEffect(() => {
    yearRef.current = year;
    monthRef.current = month;
  }, [year, month]);
  const mountedRef = useRef(true);
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Helper que aplica um payload novo de ``/blocks/`` ao state e
  // dispara o callback ``onMonthStatusChange`` quando o backend
  // entrega um ``monthStatus`` novo. **Atomicidade visual**: blocks
  // e monthStatus saem do MESMO retrato do banco — o consumer
  // (``InputsV3``) deve atualizar ``selectedMonthCanonical`` no
  // mesmo render cycle em que o tile do bloco transiciona, evitando
  // qualquer descasamento entre badge e BlocksPane.
  const applyPayload = (data) => {
    if (!data || !mountedRef.current) return;
    const incomingMonthStatus = data?.monthStatus ?? null;
    const incomingMonthStatusReason = data?.monthStatusReason ?? null;
    const incomingMonthStatusActive = data?.monthStatusActive ?? null;
    setState((prev) => ({
      ...prev,
      blocks: data?.blocks || prev.blocks,
      summary: data?.summary || prev.summary,
      monthStatus: incomingMonthStatus ?? prev.monthStatus,
      snapshotSource: data?.snapshotSource || prev.snapshotSource,
      userIsDev: data?.userIsDev ?? prev.userIsDev,
      error: null,
    }));
    if (
      typeof onMonthStatusChangeRef.current === "function" &&
      incomingMonthStatus !== null
    ) {
      onMonthStatusChangeRef.current({
        year: yearRef.current,
        month: monthRef.current,
        status: incomingMonthStatus,
        reason: incomingMonthStatusReason,
        active: incomingMonthStatusActive,
      });
    }
  };

  const refetch = () => {
    if (!canFetch || !mountedRef.current) return;
    fetchBlocksV3({
      year: yearRef.current,
      month: monthRef.current,
      etag: etagRef.current,
    })
      .then(({ data, etag, notModified }) => {
        if (!mountedRef.current) return;
        if (etag) etagRef.current = etag;
        if (notModified || !data) return;
        applyPayload(data);
      })
      .catch(() => {});
  };

  // (1) Primeira carga / mudanca de periodo.
  useEffect(() => {
    if (!canFetch) {
      setState({
        loading: false,
        blocks: [],
        summary: null,
        monthStatus: null,
        error: null,
        snapshotSource: null,
        userIsDev: false,
      });
      etagRef.current = null;
      return undefined;
    }
    let cancelled = false;
    etagRef.current = null;
    setState((prev) => ({ ...prev, loading: true, error: null }));
    fetchBlocksV3({ year, month })
      .then(({ data, etag }) => {
        if (cancelled) return;
        if (etag) etagRef.current = etag;
        const incomingMonthStatus = data?.monthStatus ?? null;
        const incomingMonthStatusReason = data?.monthStatusReason ?? null;
        const incomingMonthStatusActive = data?.monthStatusActive ?? null;
        setState({
          loading: false,
          blocks: data?.blocks || [],
          summary: data?.summary || null,
          monthStatus: incomingMonthStatus,
          error: null,
          snapshotSource: data?.snapshotSource || null,
          userIsDev: data?.userIsDev || false,
        });
        if (
          typeof onMonthStatusChangeRef.current === "function" &&
          incomingMonthStatus !== null
        ) {
          onMonthStatusChangeRef.current({
            year,
            month,
            status: incomingMonthStatus,
            reason: incomingMonthStatusReason,
            active: incomingMonthStatusActive,
          });
        }
      })
      .catch((err) => {
        if (cancelled) return;
        setState({
          loading: false,
          blocks: [],
          summary: null,
          monthStatus: null,
          error: err,
          snapshotSource: null,
          userIsDev: false,
        });
      });
    return () => {
      cancelled = true;
    };
  }, [canFetch, year, month]);

  // (2) Polling adaptativo. ``schedule()`` recalcula o intervalo a cada
  // tick conforme o estado mais recente dos blocos. Diferenca-chave vs
  // versao anterior: NUNCA pulamos o fetch. Quando tudo esta terminal,
  // apenas espacamos a cadencia (``POLL_SLOW_MS``) — o ETag/304 mantem
  // o custo trivial, e garantimos que mudancas externas (cascata de
  // ``stale`` disparada por outro evento no mes) sejam capturadas sem
  // depender de ``refetch()`` manual.
  useEffect(() => {
    if (!canFetch) return undefined;
    let cancelled = false;
    let timer = null;

    const schedule = () => {
      if (cancelled) return;
      const hasAlive = (blocksRef.current || []).some((b) =>
        ALIVE_STATUSES.has(b.status),
      );
      const interval = hasAlive ? POLL_FAST_MS : POLL_SLOW_MS;
      timer = setTimeout(tick, interval);
    };

    const tick = () => {
      if (cancelled) return;
      fetchBlocksV3({ year, month, etag: etagRef.current })
        .then(({ data, etag, notModified }) => {
          if (cancelled) return;
          if (etag) etagRef.current = etag;
          if (notModified || !data) return;
          applyPayload(data);
        })
        .catch(() => {})
        .finally(() => {
          schedule();
        });
    };

    schedule();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [canFetch, year, month]);

  // (3) Drift via sinal externo. ``monthStatusHint`` vem de outro hook
  // (tipicamente ``useProgressOverviewV3``) que continua pollando o
  // badge do mes. Se o caller detecta que o badge mudou para um estado
  // que implica trabalho pendente nos blocos (``stale``,
  // ``processing``, ``waiting``, ``action_needed``, ``system_error``),
  // forcamos refetch imediato. Evita o gap entre 304s consecutivos do
  // polling lento e o momento em que a UI principal ja sabe que houve
  // mudanca. So dispara quando o hint difere do ``monthStatus`` que ja
  // temos em maos — sem isso, o effect dispararia em todo render.
  useEffect(() => {
    if (!canFetch || !monthStatusHint) return undefined;
    if (monthStatusHint === state.monthStatus) return undefined;
    const driftStatuses = new Set([
      "stale",
      "processing",
      "waiting",
      "action_needed",
      "system_error",
      "ready_to_calculate",
    ]);
    if (!driftStatuses.has(monthStatusHint)) return undefined;
    // Onda 1.5: ``cancelled`` flag para nao aplicar payload em
    // componente desmontado (caso o ProgressDrawer feche durante a
    // request). ``mountedRef`` no ``applyPayload`` ja cobriria, mas
    // o ``cancelled`` local evita ate o setState sequer ser chamado
    // — mais barato e claro.
    let cancelled = false;
    fetchBlocksV3({ year, month, etag: etagRef.current })
      .then(({ data, etag, notModified }) => {
        if (cancelled) return;
        if (etag) etagRef.current = etag;
        if (notModified || !data) return;
        applyPayload(data);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [monthStatusHint, canFetch, year, month, state.monthStatus]);

  return { ...state, refetch };
}

export default useBlocksV3;
